import { adminBaseQuery } from '@/store/baseQuery';
import { createApi } from '@reduxjs/toolkit/query/react';

// Types based on updated swagger
export interface User {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  email_verified_at?: string | null;
  recovery_mobile_phone?: string | null;
  slug?: string;
  /** Sent as a MySQL tinyint (0/1); read it with `isFlagOn`. */
  active: boolean | number;
  status: string;
  local_trial_end_date?: string;
  title?: string;
  profile?: {
    id: number;
    slug?: string;
    occupation?: string;
    status: string;
    /** Sent as a MySQL tinyint (0/1); read it with `isFlagOn`. */
    account_status: boolean | number | string | null;
    title?: {
      id: number;
      title: string;
      is_primary: boolean;
    };
  };
  organization?: {
    id: number;
    name: string;
  };
  roles: string[];
  created_at: string;
  updated_at: string;
}

export interface UserCreateRequest {
  first_name: string;
  last_name: string;
  email: string;
  active: boolean;
  status: string;
  roles: string[];
  organization_id: number;
}

export interface UserUpdateRequest {
  first_name: string;
  last_name: string;
  active: boolean;
  status: string;
  organization_id: number;
  email_verified_at?: string | null;
  recovery_mobile_phone?: string | null;
}

export interface ChangeEmailRequest {
  email: string;
}

export interface ChangePasswordRequest {
  password: string;
}

export interface ChangeRolesRequest {
  roles: string[];
}

export interface ChangeProfileSlugRequest {
  slug: string;
}

export interface ForgotPasswordRequest {
  email: string;
}

export interface CloneProfileRequest {
  organization_id: number;
  profile_ids: number[];
  email: string;
  first_name: string;
  last_name: string;
  password?: string;
  profile_achievements_to_clone: boolean;
  profile_locations_to_clone: boolean;
  profile_phones_to_clone: boolean;
  profile_references_to_clone: boolean;
  profile_testimonials_to_clone: boolean;
  conditions_to_clone: boolean;
  condition_resources_to_clone: boolean;
  condition_prescriptions_to_clone: boolean;
  condition_recommendations_to_clone: boolean;
  assistants_to_clone: boolean;
}

export interface CloneProfileResponse {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  slug?: string;
}

export interface ProfileCondition {
  id: number;
  name: string;
  slug: string;
  description?: string | null;
  status: boolean;
  order?: number | null;
  profile_id: number;
}

export interface CopyUserConditionsRequest {
  source_user_id: number;
  condition_ids: number[];
}

export interface CopyUserConditionsResponse {
  copied_count: number;
  conditions: Array<Pick<ProfileCondition, 'id' | 'name' | 'slug' | 'profile_id'>>;
}

export interface UserDoctorResource {
  id: number;
  name: string;
  email: string;
  phone_number: string;
  created_at: string;
  updated_at: string;
}

export interface DoctorAssistantConnectionResource {
  id: number;
  doctor: UserDoctorResource;
  created_at: string;
  updated_at: string;
}

export interface CreateAssistantRequest {
  assistant_email: string;
  first_name: string;
  last_name: string;
  password?: string;
  doctor_emails: string[];
}

export interface UpdateAssistantRequest {
  first_name: string;
  last_name: string;
  doctor_emails: string[];
}

export interface ProfileConfig {
  show_condition_groups: boolean | null;
  show_condition_search: boolean | null;
  redirect_speedbump_to_condition_page: boolean | null;
  show_assistant_doctor_picker: boolean | null;
  show_enrolments: boolean | null;
  enable_dashboard_chat?: boolean | null;
  whitelabel?: any;
}

export interface UpdateProfileConfigRequest {
  show_condition_groups?: boolean | null;
  show_condition_search?: boolean | null;
  redirect_speedbump_to_condition_page?: boolean | null;
  show_assistant_doctor_picker?: boolean | null;
  show_enrolments?: boolean | null;
  enable_dashboard_chat?: boolean | null;
}

export interface UserAssistantResource {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  active: boolean;
  created_at: string;
}

export interface UsersResponse {
  data: User[];
  current_page: number;
  per_page: number;
  total: number;
  last_page: number;
  from: number;
  to: number;
}

export interface UsersFilter {
  search?: string;
  organization_id?: number;
  'roles[]'?: string[];
  sort_by?: 'id' | 'first_name' | 'last_name' | 'email' | 'created_at' | 'updated_at';
  sort_order?: 'asc' | 'desc';
  per_page?: number;
  page?: number;
}

export interface ImpersonationInitResponse {
  code: string;
  expires_at: string;
  redirect_url: string;
  impersonated_user_id: number;
}

// Utility functions
export const getUserStatusColor = (status: string | undefined | null) => {
  if (!status || typeof status !== 'string') {
    return 'gray';
  }
  switch (status.toLowerCase()) {
    case 'active': return 'green';
    case 'inactive': return 'red';
    case 'pending': return 'yellow';
    case 'verified': return 'green';
    default: return 'gray';
  }
};

export const getUserRoleDisplayName = (role: string) => {
  return role.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

// API
export const usersApi = createApi({
  reducerPath: 'usersApi',
  baseQuery: adminBaseQuery,
  tagTypes: ['User'],
  endpoints: (builder) => ({
    getUsers: builder.query<UsersResponse, UsersFilter>({
      query: (filters) => ({
        url: '/users',
        params: filters,
      }),
      providesTags: ['User'],
    }),

    getUserById: builder.query<User, number>({
      query: (id) => `/users/${id}`,
      providesTags: (result, error, id) => [{ type: 'User', id }],
    }),

    createUser: builder.mutation<User, UserCreateRequest>({
      query: (data) => ({
        url: '/users',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['User'],
    }),

    updateUser: builder.mutation<User, { id: number } & UserUpdateRequest>({
      query: ({ id, ...data }) => ({
        url: `/users/${id}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'User', id }, 'User'],
    }),

    // Admin profile route; `account_status` is not part of the user update.
    updateProfileAccountStatus: builder.mutation<unknown, { userId: number; profileId: number; accountStatus: boolean }>({
      query: ({ profileId, accountStatus }) => ({
        url: `/profiles/${profileId}`,
        method: 'PATCH',
        body: { account_status: accountStatus },
      }),
      invalidatesTags: (result, error, { userId }) => [{ type: 'User', id: userId }, 'User'],
    }),

    changeUserEmail: builder.mutation<User, { id: number } & ChangeEmailRequest>({
      query: ({ id, email }) => ({
        url: `/users/${id}/email`,
        method: 'PUT',
        body: { email },
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'User', id }, 'User'],
    }),

    changeUserPassword: builder.mutation<User, { id: number } & ChangePasswordRequest>({
      query: ({ id, password }) => ({
        url: `/users/${id}/password`,
        method: 'PUT',
        body: { password },
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'User', id }, 'User'],
    }),

    changeUserRoles: builder.mutation<User, { id: number } & ChangeRolesRequest>({
      query: ({ id, roles }) => ({
        url: `/users/${id}/roles`,
        method: 'PUT',
        body: { roles },
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'User', id }, 'User'],
    }),

    changeProfileSlug: builder.mutation<User['profile'], { profileId: number } & ChangeProfileSlugRequest>({
      query: ({ profileId, slug }) => ({
        url: `/profiles/${profileId}/slug`,
        method: 'PATCH',
        body: { slug },
      }),
      invalidatesTags: ['User'],
    }),

    sendForgotPassword: builder.mutation<{ status: boolean; message: string }, ForgotPasswordRequest>({
      query: ({ email }) => ({
        url: `/users/forgot-password`,
        method: 'POST',
        body: { email },
      }),
    }),

    cloneProfile: builder.mutation<CloneProfileResponse, CloneProfileRequest>({
      query: (data) => ({
        url: '/clone/profile',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['User'],
    }),

    getUserConditions: builder.query<ProfileCondition[], number>({
      query: (userId) => `/users/${userId}/conditions`,
    }),

    copyUserConditions: builder.mutation<
      CopyUserConditionsResponse,
      { targetUserId: number; data: CopyUserConditionsRequest }
    >({
      query: ({ targetUserId, data }) => ({
        url: `/users/${targetUserId}/copy-conditions`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (result, error, { targetUserId }) => [
        { type: 'User', id: targetUserId },
        'User',
      ],
    }),

    getAssistantDoctors: builder.query<DoctorAssistantConnectionResource[], number>({
      query: (assistantId) => `/assistants/${assistantId}/doctors`,
    }),

    createAssistant: builder.mutation<UserAssistantResource, CreateAssistantRequest>({
      query: (data) => ({
        url: '/assistants',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['User'],
    }),

    updateAssistant: builder.mutation<UserAssistantResource, { id: number } & UpdateAssistantRequest>({
      query: ({ id, ...data }) => ({
        url: `/assistants/${id}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['User'],
    }),

    getProfileConfig: builder.query<ProfileConfig, number>({
      query: (id) => `/users/${id}/profile-config`,
      providesTags: (result, error, id) => [{ type: 'User', id }],
    }),

    updateProfileConfig: builder.mutation<ProfileConfig, { id: number } & UpdateProfileConfigRequest>({
      query: ({ id, ...data }) => ({
        url: `/users/${id}/profile-config`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'User', id }, 'User'],
    }),

    impersonateUser: builder.mutation<ImpersonationInitResponse, number>({
      query: (id) => ({
        url: `/users/${id}/impersonate`,
        method: 'POST',
      }),
    }),
  }),
});

export const {
  useGetUsersQuery,
  useLazyGetUsersQuery,
  useGetUserByIdQuery,
  useCreateUserMutation,
  useUpdateUserMutation,
  useUpdateProfileAccountStatusMutation,
  useChangeUserEmailMutation,
  useChangeUserPasswordMutation,
  useChangeUserRolesMutation,
  useChangeProfileSlugMutation,
  useSendForgotPasswordMutation,
  useCloneProfileMutation,
  useGetUserConditionsQuery,
  useCopyUserConditionsMutation,
  useGetAssistantDoctorsQuery,
  useCreateAssistantMutation,
  useUpdateAssistantMutation,
  useGetProfileConfigQuery,
  useUpdateProfileConfigMutation,
  useImpersonateUserMutation,
} = usersApi;
